import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import './index.css';
/** ちいかわのおかいものアプリ
Webアプリとして無料配布可能なSPA
指定のCSV（public/chiikawa_goods.csv、public/chiikawa_baby.csv、public/mogumogu_0912.csv）を読み込み、カート管理・印刷対応
 */
function App() {
  const [goods, setGoods] = useState([]); // 商品リスト
  const [cart, setCart] = useState([]); // カート内容
  const [total, setTotal] = useState(0); // 合計金額
  const [selectedCategory, setSelectedCategory] = useState('すべて'); // 選択中のカテゴリ
  const [selectedSeries, setSelectedSeries] = useState('映画ちいかわ'); // 選択中のシリーズ
  const [quantities, setQuantities] = useState({}); // 商品ごとの入力数量
  const [confirmed, setConfirmed] = useState({}); // 決定状態

  // シリーズとCSVファイルのマッピング
  const seriesCsvMap = {
　　'映画ちいかわ': '/chiikawa_movie.csv',
    'ちいかわパーク': '/chiikawa_goods.csv',
    'CHIIKAWA BABY': '/chiikawa_baby.csv',
    'もぐもぐ本舗9/12新商品': '/mogumogu_0912.csv',
    '10/17新商品（10/10原宿）': '/chiikawa_goods_10_17.csv',
  };

  // シリーズ変更時にCSVを読み込む
  useEffect(() => {
    setCart([]);
    setQuantities({});
    setConfirmed({});
    setTotal(0);
    setSelectedCategory('すべて');

    const csvPath = seriesCsvMap[selectedSeries];
    fetch(csvPath)
      .then(response => {
        if (!response.ok) throw new Error(`${selectedSeries}のCSV読み込みに失敗しました`);
        return response.text();
      })
      .then(data => {
        Papa.parse(data, {
          header: true,
          complete: (result) => {
            // ソートを削除し、CSVの順序を保持
            const filteredGoods = result.data.filter(item => item.name && item.price);
            setGoods(filteredGoods);
          },
        });
      })
      .catch(error => {
        console.error('CSV読み込みエラー:', error);
        alert(`商品データの読み込みに失敗しました。後でもう一度お試しください。`);
      });
  }, [selectedSeries]);

  // カテゴリ一覧（CSVの登場順に並べる）
  const categories = goods.every(item => item.category === 'なし' || !item.category)
    ? ['すべて']
    : (() => {
        // CSVの登場順でカテゴリを保持
        const categoryOrder = [];
        const seen = new Set();
        goods.forEach(item => {
          const category = item.category && item.category !== 'なし' ? item.category : null;
          if (category && !seen.has(category)) {
            seen.add(category);
            categoryOrder.push(category);
          }
        });
        // 「公式サイト未掲載」を最後に移動
        const filteredCategories = categoryOrder.filter(category => category !== '公式サイト未掲載');
        return [
          'すべて',
          ...filteredCategories,
          ...(categoryOrder.includes('公式サイト未掲載') ? ['公式サイト未掲載'] : [])
        ];
      })();

  // カテゴリフィルタリング
  const filteredGoods = selectedCategory === 'すべて'
    ? goods
    : goods.filter(item => item.category === selectedCategory);

  // 数量変更ハンドラ
  const handleQuantityChange = (itemName, value) => {
    setQuantities(prev => ({
      ...prev,
      [itemName]: value,
    }));
  };

  // カートに追加
  const addToCart = (item) => {
    const quantity = parseInt(quantities[item.name]);
    if (isNaN(quantity) || quantity === 0) {
      const newCart = cart.filter(cartItem => cartItem.name !== item.name);
      setCart(newCart);
      calculateTotal(newCart);
      setQuantities(prev => ({
        ...prev,
        [item.name]: '',
      }));
      setConfirmed(prev => {
        const newConfirmed = { ...prev };
        delete newConfirmed[item.name];
        return newConfirmed;
      });
      return;
    }
    if (quantity < 1 || quantity > 10) {
      alert('数量は1から10の範囲で入力してください。');
      return;
    }
    const newCart = cart.filter(cartItem => cartItem.name !== item.name);
    newCart.push({ ...item, quantity });
    setCart(newCart);
    calculateTotal(newCart);
    setQuantities(prev => ({
      ...prev,
      [item.name]: quantity,
    }));
    setConfirmed(prev => ({ ...prev, [item.name]: true }));
  };

  // カートから商品を削除
  const removeFromCart = (indexToRemove) => {
    const itemToRemove = cart[indexToRemove];
    const newCart = cart.filter((_, index) => index !== indexToRemove);
    setCart(newCart);
    calculateTotal(newCart);
    setQuantities(prev => {
      const newQuantities = { ...prev };
      delete newQuantities[itemToRemove.name];
      return newQuantities;
    });
    setConfirmed(prev => {
      const newConfirmed = { ...prev };
      delete newConfirmed[itemToRemove.name];
      return newConfirmed;
    });
  };

  // 一括削除
  const resetAll = () => {
    setCart([]);
    setQuantities({});
    setConfirmed({});
    setTotal(0);
  };

  // 全部ください
  const addAllToCart = () => {
    const newCart = [...cart];
    const newQuantities = { ...quantities };
    const newConfirmed = { ...confirmed };
    filteredGoods.forEach(item => {
      if (!cart.some(cartItem => cartItem.name === item.name)) {
        const quantity = 1;
        newCart.push({ ...item, quantity });
        newQuantities[item.name] = quantity;
        newConfirmed[item.name] = true;
      }
    });
    setCart(newCart);
    setQuantities(newQuantities);
    setConfirmed(newConfirmed);
    calculateTotal(newCart);
  };

  // 合計金額計算
  const calculateTotal = (cart) => {
    const total = cart.reduce((sum, item) => {
      let price = item.price;
      if (typeof price === 'string') {
        price = parseFloat(price.replace(/[¥￥,"](税込)?/g, ''));
      } else {
        price = parseFloat(price);
      }
      return isNaN(price) ? sum : sum + price * item.quantity;
    }, 0);
    setTotal(total);
  };

  // 合計点数計算
  const calculateTotalItems = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  // 警告メッセージ
  const getWarningMessage = () => {
    if (total >= 500000) return 'おっきい討伐…がんばろッッ';
    if (total >= 300000) return 'なんとかなれーッ!!';
    if (total >= 100000) return 'お金もうないよッ';
    if (total >= 30000) return '悪いことしてる気分…';
    if (total >= 10000) return '買いすぎ注意！';
    return '';
  };

  // 印刷ハンドラ
  const handlePrint = () => {
    const totalItems = calculateTotalItems();
    const fontSize = totalItems >= 100 ? '10px' : totalItems >= 50 ? '11px' : '12px';
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>ちいかわのおかいもの - 購入一覧</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 10mm; }
            h1 { text-align: center; font-size: 20px; margin-bottom: 10px; }
            .table-container { column-count: 2; column-gap: 10px; }
            table { width: 100%; border-collapse: collapse; font-size: ${fontSize}; }
            th, td { border: 1px solid #ccc; padding: 2px 4px; }
            th { background-color: #f0f0f0; }
            .name-column { width: auto; }
            .price-column { width: 45px; text-align: right; line-height: 1.2; }
            .quantity-column { width: 30px; text-align: center; }
            .totals { margin-top: 10px; font-size: ${fontSize}; text-align: right; }
            .totals div { display: flex; justify-content: flex-end; gap: 20px; }
            .totals p { margin: 2px 0; }
            .signature { margin-top: 5px; text-align: right; color: #999; font-size: ${fontSize}; }
          </style>
        </head>
        <body>
          <h1>ちいかわのおかいもの - 購入一覧</h1>
          <div class="table-container">
            <table>
              <tr>
                <th class="name-column">商品名</th>
                <th class="price-column">価格<br>（税込）</th>
                <th class="quantity-column">数量</th>
              </tr>
              ${cart
                .map(
                  item => `
                <tr>
                  <td class="name-column">${item.name}</td>
                  <td class="price-column">¥${parseFloat(
                    item.price.replace(/[¥￥,"](税込)?/g, '')
                  ).toLocaleString()}</td>
                  <td class="quantity-column">${item.quantity}</td>
                </tr>
              `
                )
                .join('')}
            </table>
          </div>
          <div class="totals">
            <div>
              <p>合計点数: ${totalItems}点</p>
              <p>合計: ¥${total.toLocaleString()}（税込）</p>
            </div>
            <p class="signature">©てぃとす</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="app-container">
      <h1>ちいかわのおかいもの</h1>

      {/* シリーズ選択とカテゴリ選択 */}
      <div className="series-category-controls">
        <div className="series-select">
          <label htmlFor="series">シリーズを選択</label>
          <select
            id="series"
            value={selectedSeries}
            onChange={(e) => setSelectedSeries(e.target.value)}
          >
            {Object.keys(seriesCsvMap).map((series, index) => (
              <option key={index} value={series}>
                {series}
              </option>
            ))}
          </select>
        </div>
        <div className="category-select">
          <label htmlFor="category">カテゴリを選択</label>
          <select
            id="category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        <button className="add-all-button" onClick={addAllToCart}>
          全部ください
        </button>
      </div>

      <p className="restriction-notice">
        購入額計算アプリです。実際に購入はできません。<br />
        個数制限、売り切れ、サイトに載っていないものには対応していません。
      </p>

      {/* メインコンテンツ（商品リストとカート） */}
      <div className="main-content">
        {/* 商品リスト */}
        <div className="goods-container">
          <div className="goods-grid">
            {filteredGoods.map((item, index) => (
              <div key={index} className="goods-card">
                <h3 className="goods-name">{item.name}</h3>
                <p>価格: ¥{parseFloat(item.price.replace(/[¥￥,"](税込)?/g, '')).toLocaleString()}（税込）</p>
                <div className="quantity-container">
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={quantities[item.name] ?? ''}
                    placeholder="数量"
                    onChange={(e) => handleQuantityChange(item.name, e.target.value)}
                    className={confirmed[item.name] ? 'confirmed' : ''}
                  />
                  <button onClick={() => addToCart(item)}>決定</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* カート */}
        <div className="cart-container">
          <div className="cart">
            <div className="cart-header">
              <h2>カート</h2>
              <button className="reset-button" onClick={resetAll}>
                一括削除
              </button>
            </div>
            {cart.length === 0 ? (
              <p>カートは空です</p>
            ) : (
              <ul>
                {cart.map((item, index) => (
                  <li key={index} className="cart-item">
  <span>
    {item.name} x {item.quantity} = ¥
    {(parseFloat(item.price.replace(/[¥￥,"](税込)?/g, '')) * item.quantity).toLocaleString()}（税込）
  </span>
  <button className="delete-button" onClick={() => removeFromCart(index)}>
    削除
  </button>
</li>
                ))}
              </ul>
            )}
            <div className="cart-footer">
              <div className="total-items">
                <div>合計点数</div>
                <div>{calculateTotalItems()}点</div>
              </div>
              <div className="total-amount">
                <div>合計: ¥{total.toLocaleString()}</div>
                <div style={{ textAlign: 'right' }}>（税込）</div>
                {getWarningMessage() && <div className="warning">{getWarningMessage()}</div>}
              </div>
            </div>
          </div>
          <div className="cart-actions">
            <button className="print-button" onClick={handlePrint}>
              印刷・保存
            </button>
          </div>
        </div>
      </div>

      {/* フッター */}
      <div className="footer-notice">
        <p>
          このアプリは
          <a href="https://chiikawapark-tokyo.jp" target="_blank" rel="noopener noreferrer">
            ちいかわパーク公式サイト
          </a>
          または関連サイトから手作業で収集したデータを使用します。非公式アプリであり、商用利用は禁止です。
        </p>
        <p className="signature">©てぃとす</p>
      </div>
    </div>
  );
}

export default App;